import { getEntityTypeName } from 'globalFunctions';
import { TFunction } from 'i18next';
import Asset, { AssetType } from 'models/asset/asset';
import type { AuditCriterion } from 'models/auditCriteria';
import { EntityTypes } from 'models/entity';
import ProcessingActivity, { ProcessingActivityRole } from 'models/processingActivity';
import {
  AICriterionOutcomeResult,
  AICriterionOutcomeSourceKind,
  AICriterionOutcomeStatus,
} from 'models/auditCriteriaGeneric';

// PLAN-075 Task 14: the domain models of generic criteria sets, the copy compare, assessment tasks with the AI evidence
// audit the start card offers, and the AI outcomes. The DTOs are in models/dto/auditCriteriaGenericDTO.ts, the mappers in
// mappings/auditCriteriaGenericMapping.ts.

// The families a generic set is written for, in the order of the module cards, with the name of their settings hash
// (#auditcriteria-<name>). A requirement's criteria are its control family's.
export const genericAuditCriteriaFamilies: { entityType: EntityTypes; hashName: string }[] = [
  { entityType: EntityTypes.Control, hashName: 'control' },
  { entityType: EntityTypes.Risk, hashName: 'risk' },
  { entityType: EntityTypes.Asset, hashName: 'asset' },
  { entityType: EntityTypes.ProcessingActivity, hashName: 'processingactivity' },
  { entityType: EntityTypes.Process, hashName: 'process' },
  { entityType: EntityTypes.Objective, hashName: 'objective' },
];

export const genericAuditCriteriaHashPrefix = '#auditcriteria-';

// The family a settings hash opens, or undefined for another hash.
export function getGenericAuditCriteriaFamilyFromHash(hash: string): EntityTypes | undefined {
  if (!hash.startsWith(genericAuditCriteriaHashPrefix)) return undefined;
  const name = hash.substring(genericAuditCriteriaHashPrefix.length).toLowerCase();

  return genericAuditCriteriaFamilies.find((family) => family.hashName === name)?.entityType;
}

export function getGenericAuditCriteriaHash(entityType: EntityTypes): string {
  const family = genericAuditCriteriaFamilies.find((item) => item.entityType === entityType);

  return family ? `${genericAuditCriteriaHashPrefix}${family.hashName}` : '';
}

// The subtypes a family's generic sets can be written for: the asset types and the processing activity roles.
export function getGenericAuditCriteriaSubTypes(entityType: EntityTypes): number[] {
  switch (entityType) {
    case EntityTypes.Asset:
      return [
        AssetType.Information,
        AssetType.Physical,
        AssetType.Software,
        AssetType.Supplier,
        AssetType.People,
        AssetType.Financial,
      ];
    case EntityTypes.ProcessingActivity:
      return [ProcessingActivityRole.Controller, ProcessingActivityRole.Processor];
    default:
      return [];
  }
}

export function getGenericAuditCriteriaSubTypeText(
  entityType: EntityTypes,
  subType: number,
  t: TFunction<string[]>,
): string {
  switch (entityType) {
    case EntityTypes.Asset:
      return Asset.getTypeText(subType as AssetType, t);
    case EntityTypes.ProcessingActivity:
      return ProcessingActivity.getRoleText(subType as ProcessingActivityRole, t);
    default:
      return '';
  }
}

// The AIRunRef of an AI answer an outcome put beside the person's answer (PLAN-075 Task 23): "outcome:{id}".
export const outcomeRunRefPrefix = 'outcome:';

export function getOutcomeIdFromRunRef(aiRunRef: string | undefined): number | undefined {
  if (!aiRunRef?.startsWith(outcomeRunRefPrefix)) return undefined;
  const id = Number(aiRunRef.substring(outcomeRunRefPrefix.length));

  return Number.isInteger(id) && id > 0 ? id : undefined;
}

// A generic set's name: "<Family> (all)" for the family set, "<Family>: <subtype>" for a subtype set.
export function getGenericAuditCriteriaSetName(
  entityType: EntityTypes,
  subType: number | undefined,
  t: TFunction<string[]>,
): string {
  const family = getEntityTypeName(entityType, t);

  return subType === undefined
    ? t('criteria:Generic.FamilySet', { family: family })
    : t('criteria:Generic.SubTypeSet', {
        family: family,
        subType: getGenericAuditCriteriaSubTypeText(entityType, subType, t),
      });
}

// The key a generic set's criteria are read and written under: the family, and 0 (the family set) or -(subType + 1).
export function getGenericAuditCriteriaSetEntityId(subType: number | undefined): number {
  return subType === undefined ? 0 : -(subType + 1);
}

// One generic set of a family in the module settings list: the family set (subType undefined) or a subtype set.
export class GenericAuditCriteriaSetRow {
  auditCriteriaSetId: number = 0;

  entityType: number = 0;

  subType: number | undefined = undefined;

  activeCount: number = 0;

  modifiedOn: Date | undefined = undefined;

  reviewedOn: Date | undefined = undefined;

  headVersion: number | undefined = undefined;

  affectedEntityCount: number = 0;

  copyCount: number = 0;
}

export const auditCriteriaCompareSame = 'Same';
export const auditCriteriaCompareChanged = 'Changed';
export const auditCriteriaCompareOnlyInCopy = 'OnlyInCopy';
export const auditCriteriaCompareOnlyInSource = 'OnlyInSource';

// An item's copy beside the generic set it was copied from, criterion by criterion (by lineage).
export class AuditCriteriaCompare {
  auditCriteriaSetId: number = 0;

  sourceSetId: number | undefined = undefined;

  copiedOn: Date | undefined = undefined;

  sourceChangedOn: Date | undefined = undefined;

  rows: AuditCriteriaCompareRow[] = [];
}

export class AuditCriteriaCompareRow {
  lineageKey: string = '';

  // auditCriteriaCompare*
  changeKind: string = '';

  copy: AuditCriterion | undefined = undefined;

  source: AuditCriterion | undefined = undefined;
}

// What "Assess" creates: one task per item on its active set.
export class AssessmentTaskCreate {
  entityType: number = 0;

  entityIds: number[] = [];

  startDate: Date | undefined = undefined;

  deadline: Date | undefined = undefined;

  assigneeId: string | undefined = undefined;

  // the AI evidence audit of each item (PLAN-077) starts in the background with the new task as its consumer
  runEvidenceAudit: boolean = false;
}

export class AssessmentTaskResult {
  created: AssessmentTaskCreated[] = [];

  refused: AssessmentTaskRefusal[] = [];
}

export class AssessmentTaskCreated {
  entityType: number = 0;

  entityId: number = 0;

  taskId: number = 0;

  // the AI evidence audit the server started for the task, or why not; undefined when it was not chosen
  evidenceAudit: AssessmentEvidenceAuditStart | undefined = undefined;
}

// Why the AI evidence audit (PLAN-077) did not start: AssessmentEvidenceAuditStart.reason.
export const evidenceAuditRefusalNoCredits = 'NoCredits';
export const evidenceAuditRefusalNotAuthorized = 'NotAuthorized';
export const evidenceAuditRefusalNoSet = 'NoSet';
export const evidenceAuditRefusalNoCriteria = 'NoCriteria';
export const evidenceAuditRefusalFailed = 'Failed';

export class AssessmentEvidenceAudit {
  // the asked AI and Integration criteria the audit answers; a batch sums them per item
  criteriaCount: number = 0;

  offered: boolean = false;
}

export class AssessmentEvidenceAuditStart {
  started: boolean = false;

  // evidenceAuditRefusal*: why it did not start
  reason: string | undefined = undefined;
}

// The start card (PLAN-CTI-090): the AI evidence audit entry and the assignee pre-fill.
export class AssessmentAutomations {
  // the assignee of the item's latest assessment task, which the card pre-fills; undefined for a batch
  latestAssigneeId: string | undefined = undefined;

  // the fixed AI evidence audit entry; undefined when the server sends none
  evidenceAudit: AssessmentEvidenceAudit | undefined = undefined;
}

export class AssessmentTaskRefusal {
  entityType: number = 0;

  entityId: number = 0;

  // the server's error code (AUDIT0039 when no set resolves)
  code: string = '';

  message: string = '';
}

// An automation's result for one criterion of one item, beside the person's answer.
export class AICriterionOutcome {
  aiCriterionOutcomeId: number = 0;

  entityType: number = 0;

  entityId: number = 0;

  lineageKey: string = '';

  auditCriteriaSetId: number | undefined = undefined;

  sourceKind: AICriterionOutcomeSourceKind = AICriterionOutcomeSourceKind.ProcessRun;

  sourceRef: string = '';

  sourceName: string = '';

  proposedResult: AICriterionOutcomeResult = AICriterionOutcomeResult.Inconclusive;

  summary: string = '';

  evidenceLinkIds: number[] = [];

  status: AICriterionOutcomeStatus = AICriterionOutcomeStatus.Proposed;

  appliedToTaskId: number | undefined = undefined;

  created: Date | undefined = undefined;

  appliedOn: Date | undefined = undefined;

  clone(): AICriterionOutcome {
    return Object.assign(new AICriterionOutcome(), this, { evidenceLinkIds: [...this.evidenceLinkIds] });
  }
}
